import React from 'react'
import { tokens } from "../../theme";
import { Box, Typography, useTheme, Button, FormControl, InputLabel, Select, MenuItem, Chip } from "@mui/material";
import Header from "../../components/Header";
import Axios from 'axios';
import { motion, AnimatePresence } from "framer-motion";
// import Dropdown from '../../components/dropdown/Dropdown';
import Loading from '../../components/Loading';
import Alert from '../../components/alert/Alert';
import { useSignOut } from "react-auth-kit";
import { useNavigate, useSearchParams } from "react-router-dom";
import { InputBase, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ClearIcon from '@mui/icons-material/Clear';
import FolderIcon from '@mui/icons-material/Folder';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeIcon from '@mui/icons-material/Home';
import { Breadcrumbs, Link, Card, CardContent, CardActionArea } from '@mui/material';
import TestCard from './TestCard';
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { getAuthHeader } from '../../utils/authToken';
import ManageCategoriesModal from './ManageCategoriesModal';
import BulkDeleteTestsModal from './BulkDeleteTestsModal';
import { useTestCategories } from '../../utils/TestCategory';


const Tests = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // for 401 error
    const signOut = useSignOut();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const handleLogout = () => {
        signOut();
        localStorage.removeItem("userName");
        localStorage.removeItem("who");
        navigate("/signin");
    }

    const [loading, setLoading] = React.useState(true);
    const [tests, setTests] = React.useState(null);
    const [search, setSearch] = React.useState('');

    // for filtering and sorting
    const [filterCategory, setFilterCategory] = React.useState('');
    const [filterHasSection, setFilterHasSection] = React.useState('');
    const [filterReleaseGrades, setFilterReleaseGrades] = React.useState('');
    const [sortBy, setSortBy] = React.useState('title'); // title, title-desc, order, date, date-desc, category

    // fetch categories for filter dropdown
    const { categories } = useTestCategories();

    // for alert
    const [successAlertOpen, setSuccessAlertOpen] = React.useState(false);
    const [errorAlertOpen, setErrorAlertOpen] = React.useState(false);
    const [successAlertMessage, setSuccessAlertMessage] = React.useState("");
    const [errorAlertMessage, setErrorAlertMessage] = React.useState("");

    // for modals
    const [manageCategoriesModalOpen, setManageCategoriesModalOpen] = React.useState(false);
    const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = React.useState(false);

    // for folder view
    const [viewMode, setViewMode] = React.useState('folder'); // 'folder' or 'flat'

    // Sync currentFolder with URL query param for browser back button support
    const currentFolder = searchParams.get('folder') || null;

    // Navigate into a folder
    const openFolder = (categoryName) => {
        setSearchParams({ folder: categoryName });
    };

    // Navigate back to root
    const goToRoot = () => {
        setSearchParams({});
    };

    // Check if any actual filters are applied (for "X / total" display and Clear button)
    const hasSearchOrFilters = filterCategory !== '' || filterHasSection !== '' || filterReleaseGrades !== '' || search !== '';

    // Check if drag-and-drop should be disabled (any filter OR non-custom-order sort)
    // Drag-and-drop only works with custom order sort and no filters
    const isDragDisabled = hasSearchOrFilters || sortBy !== 'order';

    React.useEffect(() => {
        localStorage.setItem('selectedMenu', 'Tests');

        async function fetchTests () {
            await Axios.get(`${process.env.REACT_APP_URL}/api/v1/tests`, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            }).then(async (response) => {
                setTests(response.data);
            }).catch((error) => {
                if (error.response?.status === 404) {
                    setTests([]);
                    setLoading(false);
                    return;
                } else if (error.response?.status === 401) {
                    handleLogout();
                    return;
                }
                setErrorAlertMessage(error.response?.data?.message || "Something went wrong");
                setErrorAlertOpen(true);
            });
        }

        fetchTests();
        setLoading(false);
    }, []);

    const handleDragEnd = async (result) => {
        const { destination, source } = result;

        if (!destination) return;

        if (destination.droppableId === source.droppableId && destination.index === source.index) return;

        const updatedTests = Array.from(tests);
        const [removed] = updatedTests.splice(source.index, 1);
        updatedTests.splice(destination.index, 0, removed);

        try {
            await Axios.put(`${process.env.REACT_APP_URL}/api/v1/tests/order`,
                { newTestOrder: updatedTests.map((test, index) => ({ ...test, order: index + 1 })) },
                {
                    headers: {
                        Authorization: getAuthHeader(),
                    },
                }
            );

            setTests(updatedTests);
        } catch (error) {
            console.error("Error updating test order:", error);
            setErrorAlertMessage(error.response?.data?.message || "An error occurred while updating the test order");
            setErrorAlertOpen(true);
        }
    };

    const clearAllFilters = () => {
        setSearch('');
        setFilterCategory('');
        setFilterHasSection('');
        setFilterReleaseGrades('');
        setSortBy('title');
    };

    // Handle test deletion - update state without page reload
    const handleTestDelete = (testId) => {
        setTests(prevTests => prevTests.filter(test => test.id !== testId));
    };

    const getFilteredAndSortedTests = () => {
        if (!tests) return [];

        let result = [...tests];

        // Apply search filter
        if (search) {
            result = result.filter(test =>
                test.title?.toLowerCase().includes(search.toLowerCase())
            );
        }

        // Apply category filter
        if (filterCategory) {
            result = result.filter(test => test.subject === filterCategory);
        }

        // Apply has section filter
        if (filterHasSection !== '') {
            const hasSection = filterHasSection === 'true';
            result = result.filter(test => test.has_section === hasSection);
        }

        // Apply release grades filter
        if (filterReleaseGrades !== '') {
            const releaseGrades = filterReleaseGrades === 'true';
            result = result.filter(test => test.release_grades === releaseGrades);
        }

        // Apply sorting
        // Use numeric: true for natural sorting (so "Test 2" comes before "Test 10")
        const naturalCompare = (a, b) => (a || '').localeCompare(b || '', undefined, { numeric: true, sensitivity: 'base' });

        switch (sortBy) {
            case 'title':
                result.sort((a, b) => naturalCompare(a.title, b.title));
                break;
            case 'title-desc':
                result.sort((a, b) => naturalCompare(b.title, a.title));
                break;
            case 'date':
                result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                break;
            case 'date-desc':
                result.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                break;
            case 'category':
                result.sort((a, b) => naturalCompare(a.subject, b.subject));
                break;
            case 'order':
            default:
                // Keep original order from server
                break;
        }

        return result;
    };

    const filteredTests = getFilteredAndSortedTests();

    // Group tests by category for folder view
    const getTestsByCategory = () => {
        const grouped = {};

        // Initialize with all registered categories (even empty ones)
        categories.forEach(cat => {
            grouped[cat.value] = [];
        });
        grouped['Uncategorized'] = [];

        // Get list of valid category values
        const validCategories = new Set(categories.map(cat => cat.value));

        // Group filtered tests - unrecognized subjects go to Uncategorized
        filteredTests.forEach(test => {
            const subject = test.subject;
            if (subject && validCategories.has(subject)) {
                grouped[subject].push(test);
            } else {
                grouped['Uncategorized'].push(test);
            }
        });

        // Remove empty categories (except when no filter is active)
        if (filterCategory || search || filterHasSection || filterReleaseGrades) {
            Object.keys(grouped).forEach(key => {
                if (grouped[key].length === 0) {
                    delete grouped[key];
                }
            });
        }

        return grouped;
    };

    const testsByCategory = getTestsByCategory();

    // Sort folder entries based on current sortBy setting
    const sortedFolderEntries = React.useMemo(() => {
        const entries = Object.entries(testsByCategory);
        const naturalCompare = (a, b) => (a || '').localeCompare(b || '', undefined, { numeric: true, sensitivity: 'base' });

        switch (sortBy) {
            case 'title':
            case 'category':
                return [...entries].sort((a, b) => naturalCompare(a[0], b[0]));
            case 'title-desc':
                return [...entries].sort((a, b) => naturalCompare(b[0], a[0]));
            case 'date':
                return [...entries].sort((a, b) => {
                    const newestA = a[1].length > 0 ? Math.max(...a[1].map(t => new Date(t.createdAt).getTime())) : 0;
                    const newestB = b[1].length > 0 ? Math.max(...b[1].map(t => new Date(t.createdAt).getTime())) : 0;
                    return newestB - newestA;
                });
            case 'date-desc':
                return [...entries].sort((a, b) => {
                    const oldestA = a[1].length > 0 ? Math.min(...a[1].map(t => new Date(t.createdAt).getTime())) : Infinity;
                    const oldestB = b[1].length > 0 ? Math.min(...b[1].map(t => new Date(t.createdAt).getTime())) : Infinity;
                    return oldestA - oldestB;
                });
            case 'order':
            default:
                return entries;
        }
    }, [testsByCategory, sortBy]);

    return (
        <Box m="20px" marginX={3} marginY={3} >
            <Header title="Test Library" subtitle="A ONE Institute" />
            {loading || tests === null ?
                <Loading />
            : <>
                {/* Toolbar: Search, filters, and action buttons */}
                <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
                    <Box
                        display="flex"
                        backgroundColor={colors.primary[600]}
                        borderRadius="3px"
                        boxShadow={10}
                        maxWidth={250}
                        minWidth={180}
                    >
                        <InputBase
                            sx={{ ml: 2, flex: 1 }}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search..."
                        />
                        <IconButton type="button" sx={{ p: 1 }}>
                            <SearchIcon />
                        </IconButton>
                    </Box>

                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel>Category</InputLabel>
                        <Select
                            value={filterCategory}
                            label="Category"
                            onChange={(e) => setFilterCategory(e.target.value)}
                        >
                            <MenuItem value="">All Categories</MenuItem>
                            {categories.map((cat) => (
                                <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Sections</InputLabel>
                        <Select
                            value={filterHasSection}
                            label="Sections"
                            onChange={(e) => setFilterHasSection(e.target.value)}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="true">With Sections</MenuItem>
                            <MenuItem value="false">No Sections</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Grades Released</InputLabel>
                        <Select
                            value={filterReleaseGrades}
                            label="Grades Released"
                            onChange={(e) => setFilterReleaseGrades(e.target.value)}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="true">Released</MenuItem>
                            <MenuItem value="false">Not Released</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Sort By</InputLabel>
                        <Select
                            value={sortBy}
                            label="Sort By"
                            onChange={(e) => setSortBy(e.target.value)}
                        >
                            <MenuItem value="title">Title (A-Z)</MenuItem>
                            <MenuItem value="title-desc">Title (Z-A)</MenuItem>
                            <MenuItem value="category">Category</MenuItem>
                            <MenuItem value="date">Newest First</MenuItem>
                            <MenuItem value="date-desc">Oldest First</MenuItem>
                            <MenuItem value="order">Custom Order</MenuItem>
                        </Select>
                    </FormControl>

                    {hasSearchOrFilters && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ClearIcon />}
                            onClick={clearAllFilters}
                            sx={{ height: 40 }}
                        >
                            Clear
                        </Button>
                    )}

                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        {filteredTests.length} test{filteredTests.length !== 1 ? 's' : ''}
                        {hasSearchOrFilters && ` / ${tests.length}`}
                    </Typography>

                    <Box sx={{ flexGrow: 1 }} />

                    {/* View mode toggle */}
                    <Box sx={{ display: 'flex', gap: 0.5, mr: 1, border: `1px solid ${colors.grey[600]}`, borderRadius: 1, p: 0.25 }}>
                        <IconButton
                            size="small"
                            onClick={() => setViewMode('folder')}
                            sx={{
                                backgroundColor: viewMode === 'folder' ? colors.blueAccent[700] : 'transparent',
                                color: viewMode === 'folder' ? colors.grey[100] : colors.grey[400],
                                borderRadius: 0.5,
                                '&:hover': {
                                    backgroundColor: viewMode === 'folder' ? colors.blueAccent[600] : colors.grey[700],
                                }
                            }}
                            title="Folder View"
                        >
                            <FolderIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={() => setViewMode('flat')}
                            sx={{
                                backgroundColor: viewMode === 'flat' ? colors.blueAccent[700] : 'transparent',
                                color: viewMode === 'flat' ? colors.grey[100] : colors.grey[400],
                                borderRadius: 0.5,
                                '&:hover': {
                                    backgroundColor: viewMode === 'flat' ? colors.blueAccent[600] : colors.grey[700],
                                }
                            }}
                            title="Flat View"
                        >
                            <ViewModuleIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        startIcon={<SettingsIcon />}
                        onClick={() => setManageCategoriesModalOpen(true)}
                    >
                        Manage Categories
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        size="small"
                        startIcon={<DeleteSweepIcon />}
                        onClick={() => setBulkDeleteModalOpen(true)}
                    >
                        Bulk Delete
                    </Button>
                </Box>

                {isDragDisabled && viewMode === 'flat' && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Drag-and-drop reordering is disabled while filters or sorting are active.
                    </Typography>
                )}

                {tests.length === 0 && (
                    <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
                        <h1>No tests found</h1>
                    </Box>
                )}

                {/* Folder View */}
                {viewMode === 'folder' && tests.length > 0 && (
                    <Box>
                        {/* Breadcrumb Navigation */}
                        <Breadcrumbs
                            separator={<NavigateNextIcon fontSize="small" />}
                            sx={{ mb: 2 }}
                        >
                            <Link
                                component="button"
                                variant="body1"
                                onClick={goToRoot}
                                underline="hover"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    color: currentFolder ? colors.grey[400] : colors.blueAccent[400],
                                    cursor: 'pointer',
                                    fontWeight: currentFolder ? 400 : 600,
                                }}
                            >
                                <HomeIcon fontSize="small" />
                                Test Library
                            </Link>
                            {currentFolder && (
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: colors.blueAccent[400],
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                    }}
                                >
                                    <FolderIcon fontSize="small" />
                                    {categories.find(c => c.value === currentFolder)?.label || currentFolder}
                                </Typography>
                            )}
                        </Breadcrumbs>

                        {/* Root Level - Show Folder Cards */}
                        {currentFolder === null && (
                            <Box
                                display="grid"
                                gridTemplateColumns="repeat(auto-fill, minmax(250px, 1fr))"
                                gap={2}
                            >
                                <AnimatePresence>
                                    {sortedFolderEntries.map(([categoryName, categoryTests]) => {
                                        // Skip empty categories when filtering
                                        if (categoryTests.length === 0 && (filterCategory || search)) return null;

                                        const categoryInfo = categories.find(c => c.value === categoryName);

                                        return (
                                            <motion.div
                                                key={categoryName}
                                                animate={{ opacity: 1 }}
                                                initial={{ opacity: 0 }}
                                                exit={{ opacity: 0 }}
                                                layout
                                            >
                                                <Card
                                                    sx={{
                                                        minWidth: 260,
                                                        maxWidth: 260,
                                                        minHeight: 230,
                                                        maxHeight: 230,
                                                        backgroundColor: colors.primary[600],
                                                        boxShadow: "0px 0px 10px 0px rgba(0,0,0,0.5)",
                                                        border: `1px solid ${colors.primary[600]}`,
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        ":hover": {
                                                            boxShadow: "0px 0px 15px 0px rgba(0,0,0,0.6)",
                                                            border: `1px solid ${colors.blueAccent[400]}`,
                                                        }
                                                    }}
                                                >
                                                    <CardActionArea
                                                        onClick={() => openFolder(categoryName)}
                                                        sx={{
                                                            height: '100%',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            p: 3,
                                                            // Override default MUI hover overlay
                                                            '&:hover .MuiCardActionArea-focusHighlight': {
                                                                opacity: 0,
                                                            },
                                                        }}
                                                    >
                                                        <FolderIcon
                                                            sx={{
                                                                fontSize: 80,
                                                                color: colors.blueAccent[400],
                                                                mb: 2,
                                                            }}
                                                        />
                                                        <Typography
                                                            variant="h5"
                                                            sx={{
                                                                fontWeight: 600,
                                                                textAlign: 'center',
                                                                mb: 1,
                                                            }}
                                                        >
                                                            {categoryInfo?.label || categoryName}
                                                        </Typography>
                                                        <Chip
                                                            label={`${categoryTests.length} test${categoryTests.length !== 1 ? 's' : ''}`}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: colors.grey[950],
                                                            }}
                                                        />
                                                    </CardActionArea>
                                                </Card>
                                            </motion.div>
                                        );
                                    })}
                                </AnimatePresence>
                            </Box>
                        )}

                        {/* Inside Folder - Show Test Cards */}
                        {currentFolder !== null && (
                            <Box>
                                {testsByCategory[currentFolder]?.length === 0 ? (
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        justifyContent="center"
                                        alignItems="center"
                                        height="30vh"
                                    >
                                        <FolderIcon sx={{ fontSize: 60, color: colors.grey[500], mb: 2 }} />
                                        <Typography color="text.secondary">
                                            No tests in this category
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box
                                        display="grid"
                                        gridTemplateColumns="repeat(auto-fill, minmax(250px, 1fr))"
                                        gap={2}
                                    >
                                        <AnimatePresence>
                                            {testsByCategory[currentFolder]?.map((test) => (
                                                <motion.div
                                                    key={test.id}
                                                    animate={{ opacity: 1 }}
                                                    initial={{ opacity: 0 }}
                                                    exit={{ opacity: 0 }}
                                                    layout
                                                >
                                                    <TestCard
                                                        test={test}
                                                        setErrorAlertMessage={setErrorAlertMessage}
                                                        setErrorAlertOpen={setErrorAlertOpen}
                                                        setSuccessAlertMessage={setSuccessAlertMessage}
                                                        setSuccessAlertOpen={setSuccessAlertOpen}
                                                        onDelete={handleTestDelete}
                                                    />
                                                </motion.div>
                                            ))}
                                        </AnimatePresence>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                )}

                {/* Flat View (original) */}
                {viewMode === 'flat' && tests.length > 0 && (
                    <DragDropContext onDragEnd={isDragDisabled ? () => {} : handleDragEnd}>
                        <Droppable droppableId="testList" direction="horizontal" isDropDisabled={isDragDisabled}>
                            {(provided, snapshot) => (
                                <Box
                                    display="grid"
                                    gridTemplateColumns="repeat(auto-fill, minmax(250px, 1fr))"
                                    rowGap="1rem"
                                    columnGap="1rem"
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                >
                                    <AnimatePresence>
                                        {filteredTests.map((test, index) => (
                                            <Draggable
                                                key={test.id}
                                                draggableId={test.id.toString()}
                                                index={index}
                                                isDragDisabled={isDragDisabled}
                                            >
                                                {(provided, snapshot) => (
                                                    <Box
                                                        key={test.id}
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        sx={{
                                                            backgroundColor: snapshot.isDragging ? "LightPink" : colors.primary[50],
                                                            cursor: isDragDisabled ? 'default' : 'grab',
                                                        }}
                                                    >
                                                        <TestCard
                                                            test={test}
                                                            setErrorAlertMessage={setErrorAlertMessage}
                                                            setErrorAlertOpen={setErrorAlertOpen}
                                                            setSuccessAlertMessage={setSuccessAlertMessage}
                                                            setSuccessAlertOpen={setSuccessAlertOpen}
                                                            onDelete={handleTestDelete}
                                                        />
                                                    </Box>
                                                )}
                                            </Draggable>
                                        ))}
                                    </AnimatePresence>
                                    {provided.placeholder}
                                </Box>
                            )}
                        </Droppable>
                    </DragDropContext>
                )}
            </>}
            <ManageCategoriesModal
                open={manageCategoriesModalOpen}
                onClose={() => setManageCategoriesModalOpen(false)}
                onCategoriesUpdated={() => {
                    // Optionally refresh tests if needed
                }}
                setSuccessAlertMessage={setSuccessAlertMessage}
                setSuccessAlertOpen={setSuccessAlertOpen}
                setErrorAlertMessage={setErrorAlertMessage}
                setErrorAlertOpen={setErrorAlertOpen}
            />
            <BulkDeleteTestsModal
                open={bulkDeleteModalOpen}
                onClose={() => setBulkDeleteModalOpen(false)}
                onTestsDeleted={() => {
                    // Refresh tests after bulk delete
                    Axios.get(`${process.env.REACT_APP_URL}/api/v1/tests`, {
                        headers: {
                            Authorization: getAuthHeader(),
                        },
                    }).then((response) => {
                        setTests(response.data);
                    }).catch((error) => {
                        if (error.response?.status === 404) {
                            setTests([]);
                        }
                    });
                }}
                setSuccessAlertMessage={setSuccessAlertMessage}
                setSuccessAlertOpen={setSuccessAlertOpen}
                setErrorAlertMessage={setErrorAlertMessage}
                setErrorAlertOpen={setErrorAlertOpen}
            />
            <Alert
                successAlertOpen={successAlertOpen}
                setSuccessAlertOpen={setSuccessAlertOpen}
                errorAlertOpen={errorAlertOpen}
                setErrorAlertOpen={setErrorAlertOpen}
                successMsg={successAlertMessage}
                errorMsg={errorAlertMessage}
                vertical="bottom"
                horizontal="left"
            />
        </Box>
    )
}

export default Tests